import { showNotification } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import useTranslation from 'next-translate/useTranslation';
import { v4 as uuidv4 } from 'uuid';

import { FEATURE } from '~/common/enums/feature.enum';
import { reportEvent } from '~/domains/analytics';
import { MessageKeys } from '~/domains/payments/deposits/components/consts';
import { useWalletBalance } from '~/domains/payments/hooks/useWalletBalance';
import { useWalletRefundableBreakdown } from '~/domains/payments/hooks/useWalletRefundableBreakdown';
import { invalidateDepositBonuses } from '~/domains/payments/deposits/utils/query-invalidation';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { createVenmoHandle, depositToWallet } from '~/services/wallet/wallet.service.api';
import type { IPostWalletsDepositsResponse } from '~/services/wallet/interfaces/wallets.interface';
import { PaymentType } from '~/services/wallet/interfaces/wallets.interface';
import { getErrorTranslations } from '~/domains/payments/utils/utils';

import { isCustomDeposit } from './consts';

function useDepositWithVenmo({
  setCompletedDepositAmount,
}: {
  setCompletedDepositAmount?: (amount: number, response?: IPostWalletsDepositsResponse) => void;
}) {
  const { t } = useTranslation('payments');
  const queryClient = useQueryClient();
  const { refetch: refetchWalletBalance } = useWalletBalance();
  const { refetch: refetchRefundableBreakdown } = useWalletRefundableBreakdown();
  const usePayPalInternalSimulator = useIsFeatureEnabled(FEATURE.USE_PAYPAL_INTERNAL_SIMULATOR);

  return useMutation({
    mutationFn: async ({
      depositAmountWithFees,
      merchantRefNum = uuidv4(),
      contestIdForPostDeposit,
      fallbackUrl,
    }: {
      depositAmount: number;
      depositAmountWithFees: number;
      merchantRefNum?: string;
      contestIdForPostDeposit?: string;
      fallbackUrl: string;
      bonusId?: string;
      redirectTo?: string;
    }) =>
      createVenmoHandle({
        amount: depositAmountWithFees,
        currency: 'USD',
        merchantRefNum,
        simulator: usePayPalInternalSimulator ? 'INTERNAL' : 'EXTERNAL',
        transactionType: 'PAYMENT',
        returnLinks: [
          {
            rel: 'default',
            href:
              fallbackUrl?.startsWith('/') || !fallbackUrl
                ? `${window.location.origin}${fallbackUrl}`
                : fallbackUrl,
            method: 'GET',
          },
          {
            rel: 'on_completed',
            href: `${window.location.origin}/account/wallet/deposit/success?type=${
              PaymentType.VENMO
            }${contestIdForPostDeposit ? `&contestId=${contestIdForPostDeposit}` : ''}`,
            method: 'POST',
          },
          {
            rel: 'on_failed',

            href: `${window.location.origin}/account/wallet/deposit?message=${MessageKeys.REJECTED}&type=Venmo`,
            method: 'POST',
          },
        ],
      }),
    onSuccess: (response, payload) => {
      if (response.data.links) {
        localStorage.setItem(
          'depositData',
          JSON.stringify({
            paymentHandleId: response.data.id,
            paymentHandleToken: response.data.paymentHandleToken,
            paymentType: PaymentType.VENMO,
            amount: payload.depositAmountWithFees,
            displayAmount: payload.depositAmount,
            merchantRefNum: payload.merchantRefNum,
            bonusId: payload.bonusId,
            redirectTo: payload.redirectTo,
          })
        );
        const redirectLink = response.data.links[0].href;
        window.location.href = redirectLink;
      } else if (response.data.status === 'PAYABLE') {
        return depositToWallet({
          amount: payload.depositAmountWithFees,
          currency: 'USD',
          merchantRefNum: payload.merchantRefNum,
          paymentHandleId: response.data.id,
          paymentHandleToken: response.data.paymentHandleToken,
          paymentType: PaymentType.VENMO,
          ...(payload.bonusId ? { bonusId: payload.bonusId } : {}),
        }).then((depositResponse) => {
          reportEvent('Transactions > Deposit', {
            deposit_id: depositResponse.id,
            deposit_method: 'Venmo',
            deposit_amount: payload.depositAmount,
            is_custom_deposit: isCustomDeposit(payload.depositAmount),
            currency: 'USD',
            bonus_id: payload.bonusId,
          });
          refetchWalletBalance();
          refetchRefundableBreakdown();
          invalidateDepositBonuses(queryClient, payload.bonusId);
          setCompletedDepositAmount?.(payload.depositAmount, depositResponse);
        });
      } else {
        showNotification({
          // @ts-expect-error Missing types for error response in Swagger
          title: response.error,
          // @ts-expect-error Missing types for error response in Swagger
          message: response?.data?.message || response.message,
          color: 'red',
          autoClose: 5000,
        });
      }
    },
    onError: (error: AxiosError<{ message: string }>) => {
      const errorMessage = getErrorTranslations(t, error.code);

      if (error.code === 'ERR_BAD_REQUEST') {
        errorMessage.title = t('deposits.errors.venmo');
        errorMessage.message = error.response?.data?.message ?? '';
      }

      showNotification({
        title: errorMessage.title,
        message: errorMessage.message,
        color: 'red',
        autoClose: 5000,
      });
    },
  });
}

export { useDepositWithVenmo };
